// The right edge: what a drag does and the camera's own tiles (PLAN-UI §28.3,
// U5).

import type { CameraPrefs } from "../cameraPrefs";
import { ToolRail } from "../ToolRail";
import type { Tool } from "../tool";
import { useCameraKeys } from "../useCameraKeys";
import { viewActions, type ViewAction } from "../viewActions";
import { useBoardHandle } from "../shell/BoardHandle";
import { useShellMode } from "../shell/ShellMode";

export function RailZone({
  tool,
  onTool,
  camera,
  onCamera
}: {
  /** @deprecated Unused since the orders flag was retired (§31 R1). */
  board_?: unknown;
  tool: Tool;
  onTool: (tool: Tool) => void;
  /** What the player remembers about the view — the tilt is this zone's half. */
  camera: CameraPrefs;
  onCamera: (patch: Partial<CameraPrefs>) => void;
}) {
  const board = useBoardHandle();
  const { watching } = useShellMode();

  const actions = viewActions({ tilt: camera.tilt });

  /**
   * What an action does, whether a finger or a key asked for it — **one
   * mapping**, since a tile and its accelerator that disagree is two controls
   * wearing one label.
   *
   * **The pref, not the rig, for the tilt**: it is remembered and pushed into
   * the canvas as a prop, so a reload opens on the pitch you chose — the other
   * three are moments and nothing stores them.
   */
  const runAction = (id: ViewAction["id"]) => {
    if (id === "view-rotate-back") board.current?.rotate(-1);
    else if (id === "view-rotate") board.current?.rotate(1);
    else if (id === "view-tilt") onCamera({ tilt: camera.tilt === "top" ? "table" : "top" });
    else board.current?.fitView();
  };

  /**
   * The keys, here rather than in the shell: they are the same four controls
   * as the tiles beside them, so they are read and changed together — the way
   * the transport's arrows live in the transport's own zone.
   */
  useCameraKeys({ board, actions, run: runAction });

  return (
    /* **Always**, watching included — the rail is how you look at a board,
       which is the whole of the activity in a game nobody is playing, so it
       drops to Pieces and the camera group rather than going away. That is the
       same whole-seat exception the transport takes, and `railTools` owns it. */
    <ToolRail
      tool={tool}
      onTool={onTool}
      watching={watching}
      actions={actions}
      onAction={runAction}
    />
  );
}
